import { useEffect, useRef, useState } from 'react'

/**
 * Animates a number toward its target over ~280ms, ease-out, on every change.
 * Under prefers-reduced-motion the value just jumps. Pair with `.tnum` so the
 * digits do not wobble the layout while they run.
 */
export function useCountUp(target: number, durationMs = 280): number {
  const [shown, setShown] = useState(target)
  const latest = useRef(target)

  useEffect(() => {
    const from = latest.current
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || from === target) {
      latest.current = target
      setShown(target)
      return
    }
    const start = performance.now()
    let frame = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      const eased = 1 - (1 - t) ** 3
      const value = from + (target - from) * eased
      latest.current = value
      setShown(value)
      if (t < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, durationMs])

  return shown
}
